import dayjs from 'dayjs';
import { decodeHTML } from 'entities';
import { useArticleStore } from '~/store/article';
export default function useArticle () {
  const article = useArticleStore().currentArticle || {};

  function getDescFromBody (maxLength = 270) {
    let remainingLength = maxLength;
    let processedText = '';
    const body = article?.body || [];
    for (let i = 0; i < body.length; i++) {
      const item = body[i];
      if (item.type === 'text' && remainingLength > 0) {
        const plainText = item.html ? item.html.replace(/<[^>]+>/g, '') : ''; // Remove HTML tags
        const list = plainText.split('.').filter(Boolean) || [];

        for (const line of list) {
          if (remainingLength > 0 && processedText.length + line.length + 1 <= maxLength) {
            processedText += `${line}.`;
            remainingLength = maxLength - processedText.length;
          } else {
            break; // Stop processing when remainingLength is 0 or maxLength is reached
          }
        }
      }
    }

    return processedText;
  }

  const getMetaDescription = () => article?.intro || getDescFromBody(270);
  const getMetaTitle = () => decodeHTML(article?.seoTitle || article?.title || '');

  const getMetaImage = (fallback = '') => article?.socialMediaAsset?.src
    ? article?.socialMediaAsset
    : article?.leadAsset?.type === 'picture'
      ? article?.leadAsset
      : article?.body?.find(i => i.type === 'picture') || { src: fallback, imageCrops: {} };

  const getSoMeTitle = (suffix = null) => {
    const title = decodeHTML(article?.headFront || article?.title || '');

    return article.accessLevel === 'paywall' && suffix ? `${title} ${suffix}` : title;
  };

  const getPaywallMeta = () => {
    const meta = [
      {
        name: 'paywall:module',
        content: `${import.meta.env.PUBLICATION || 'cpp'}-article`
      },
      {
        name: 'paywall:article_type',
        content: 'cpp'
      }
    ];
    if (article?.metadata?.pids) {
      const pids = (typeof article.metadata.pids === 'string' ? article.metadata.pids.split(',') : article.metadata.pids) || [];
      for (let i = 0; i < pids.length; i++) {
        meta.push({
          name: 'paywall:pids',
          content: pids[i],
          idx: i
        });
      }
    }

    return meta;
  };

  const getPDFButtonLink = async () => {
    const date = article?.metadata?.scheduledDateOnPrint || dayjs(article?.metadata?.actualPrintDate).format('YYYY-MM-DD');
    const page = article?.metadata?.pageNumberPrint || article?.metadata?.actualPrintPage;
    const rtConfig = useRuntimeConfig().public;
    try {
      if (date && page && rtConfig.printEdition?.api) {
        const printEdition = await $fetch(useUrlReplace().replaceUrl(rtConfig.printEdition.api, { date, page }));
        if (printEdition?.editionNumber && printEdition?.pageNumber) {
          return useUrlReplace().replaceUrl(rtConfig.printEdition.visioLink, {
            utgave: printEdition.editionNumber,
            page: printEdition.pageNumber
          });
        }
      }
    } catch (e) {
      throw createError({ statusCode: 404, statusMessage: (e as Error).message });
    }
    return '';
  };

  const bylineMedia = computed<Record<string, any>>(() => {
    return article?.byline?.authors?.length > 1
      ? {}
      : {
          src: article.byline.authors[0]?.options?.profileImage,
          alt: article.byline.authors[0]?.name || '',
          fetchPriority: 'high',
          loadingMode: 'eager',
          ratio: '1x1',
          showImage: true
        };
  });

  const bylineCommonDetailsItems = computed<Record<string, any>>(() => {
    if (!article?.byline) {
      return {};
    }
    const { options: bylineOptions, authors } = article.byline;
    if (!authors || authors.length === 0) {
      return {};
    }

    if (article.subType === 'commentary') {
      const signOffText = bylineOptions?.signOffText ? bylineOptions.signOffText.replace(/(<([^>]+)>)/gi, '') : '';
      const authorProfileText = authors?.length > 1 ? signOffText : (authors?.[0]?.options?.alternativeBylineProfileText) || signOffText;
      return {
        dateline: null,
        commonDetails: authorProfileText
      };
    }

    if (article.subType === 'featureArticle') {
      return {
        dateline: article?.metadata?.dateline || bylineOptions?.publishedLocation || null,
        commonDetails: null
      };
    }
    return {
      dateline: article?.metadata?.dateline || bylineOptions?.publishedLocation || null,
      commonDetails: article?.byline?.authors?.length === 1 && article?.byline?.authors?.[0]?.options ? article.byline.authors[0].options.profileTitle : null
    };
  });

  const bylineActionItems = computed<Record<string, any>>(() => {
    const authors = article?.byline?.authors;
    if (authors?.length > 1) {
      return [{ showAuthorAlertSubscription: false }];
    }

    const INVALID_AUTHORS_FOR_NEWSLETTER = useRuntimeConfig().public.invalidAuthorsForNewsletter || [];
    return [{
      showAuthorAlertSubscription: authors?.[0]?.email && authors?.[0]?.sourceId && authors?.[0]?.name &&
          !INVALID_AUTHORS_FOR_NEWSLETTER.includes(authors?.[0]?.name),
      name: authors?.[0]?.name,
      username: authors?.[0]?.userName
    }];
  });

  const bylineItems = computed(() => {
    const items = [];
    if (article?.byline?.authors) {
      for (const author of article.byline.authors) {
        let titleAnchorLink = author.email ? `mailto:${author.email}` : null;
        if (author.hasAuthorPage) {
          titleAnchorLink = `/author/${author.userName}`;
        }
        const typeMap = {
          author: 'Tekst',
          photographer: 'Foto',
          video: 'Video',
          illustration: 'Illustrasjon',
          stylist: 'Stylist'
        };
        const details =
          article?.subType === 'featureArticle'
            ? typeMap[author.type as keyof typeof typeMap] ?? ''
            : null;
        items.push({
          title: author.name || '',
          titleAnchorLink,
          details
        });
      }
    }
    return items;
  });

  const bylineTrackEventData = computed(() => {
    return article?.byline?.authors
      ? {
          eventName: 'Byline',
          ...article.byline.authors
        }
      : {};
  });

  const isOpinionArticle = computed(() => article?.subType === 'commentary');

  return {
    isOpinionArticle,
    bylineTrackEventData,
    bylineCommonDetailsItems,
    bylineActionItems,
    bylineMedia,
    bylineItems,
    getMetaTitle,
    getMetaDescription,
    getMetaImage,
    getSoMeTitle,
    getPaywallMeta,
    getPDFButtonLink
  };
}
